import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrgNavigation, extractBasePath } from '../../utils/navigation';
import {
  Grid4x4 as UnitsIcon,
  Settings as SiteConfigIcon,
  LocationOn as AccessControlIcon,
  Hub as ChargingSessionsIcon,
  Schedule as BillingIcon,
} from '@mui/icons-material';

interface NavigationBarProps {
  // Add props as needed
}

const NavigationBar: React.FC<NavigationBarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateToOrgPath } = useOrgNavigation();

  const handleItemClick = (path: string) => {
    // Get the current project ID from the URL
    const basePath = extractBasePath(location.pathname);
    const pathSegments = basePath.split('/');
    const projectId = pathSegments[2]; // /projects/projectId/...
    const fullPath = `/projects/${projectId}/${path}`;
    const orgPath = navigateToOrgPath(fullPath);
    navigate(orgPath);
  };

  const isActive = (path: string) => {
    const basePath = extractBasePath(location.pathname);
    return basePath.endsWith(path);
  };
  return (
    <Box sx={{
      backgroundColor: (theme) => theme.palette.secondary.dark + 'BF', // 75% opacity
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      px: 2,
      py: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      height: '36px'
    }}>
      {/* Navigation items */}
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0, height: '100%' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('assets') ? 0 : 1,
            backgroundColor: isActive('assets') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('access-control') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('assets')}
        >
          <UnitsIcon sx={{ 
            color: 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white'
            }}
          >
            Assets
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('site-config') ? 0 : 1,
            backgroundColor: isActive('site-config') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('access-control') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('site-config')}
        >
          <SiteConfigIcon sx={{ 
            color: isActive('site-config') ? 'white' : 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white',
            }}
          >
            Site Config
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('access-control') ? 0 : 1,
            backgroundColor: isActive('access-control') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('access-control') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('access-control')}
        >
          <AccessControlIcon sx={{ 
            color: 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white',
            }}
          >
            RFID Access
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('charging-sessions') ? 0 : 1,
            backgroundColor: isActive('charging-sessions') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('charging-sessions') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('charging-sessions')}
        >
          <ChargingSessionsIcon sx={{ 
            color: 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white',
            }}
          >
            Charging Sessions
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('billing-invoices') ? 0 : 1,
            backgroundColor: isActive('billing-invoices') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('billing-invoices') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('billing-invoices')}
        >
          <BillingIcon sx={{ 
            color: 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white',
            }}
          >
            Billing / Invoices
          </Typography>
        </Box>
      </Box>


    </Box>
  );
};

export default NavigationBar;
